import { ACTIVE_API, API_CONFIG, API_KEY } from '../config';

// Helper function to handle fetch responses
const handleResponse = async (response) => {
  if (!response.ok) {
    throw new Error(`Erreur HTTP: ${response.status}`);
  }
  
  // Check if response is JSON
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return await response.json();
  }
  
  return await response.text();
};

// Headers with API key
const getHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY
  };
};

// Fetch GET wrapper
const fetchGet = async (url) => {
  const response = await fetch(url, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

// Fetch POST wrapper
const fetchPost = async (url, data) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

const apiService = {
  // Fetch financial report data for a tenant
  getFinancialReport: async (tenantId) => {
    try {
      const url = ACTIVE_API.FINANCIAL_REPORT(tenantId);
      return await fetchGet(url);
    } catch (error) {
      console.error('Error fetching financial report:', error);
      throw error;
    }
  },

  // Update note for a record
  updateNote: async (recordID, fieldName, value, tableName) => {
    try {
      let data;
      
      // Le format varie entre les deux APIs
      if (!API_CONFIG.OTHERS_USE_NEW_API) {
        data = {
          id: recordID,
          table_name: tableName
        };
        data[fieldName] = value;
      } else {
        data = {
          recordID,
          fieldName,
          value,
          tableName
        };
      }
      
      return await fetchPost(ACTIVE_API.UPDATE_NOTE, data);
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  // Update closure status
  updateClotureStatus: async (recordID, isChecked) => {
    try {
      // Format pour l'API n8n - endpoint add-note-to-record
      const data = {
        id: recordID,
        table_name: 'locataires',
        cloturer: isChecked
      };
      
      console.log("Mise à jour du statut de clôture:", data);
      console.log("URL de l'API:", ACTIVE_API.UPDATE_CLOTURE);
      
      // Vérification de l'ID
      if (!recordID) {
        throw new Error("ID du locataire manquant");
      }
      
      // Assurez-vous que le paramètre isChecked est un booléen
      const formattedData = {
        ...data,
        cloturer: !!isChecked  // Force la conversion en booléen
      };
      
      console.log("Données formatées envoyées:", formattedData);
      
      return await fetchPost(ACTIVE_API.UPDATE_CLOTURE, formattedData);
    } catch (error) {
      console.error('Error updating closure status:', error);
      throw error;
    }
  },

  // Generate receipts
  generateReceipts: async (bailID) => {
    try {
      console.log("Génération des quittances pour bail:", bailID);
      
      // Toujours utiliser la version GET pour n8n
      const endpoint = ACTIVE_API.GENERATE_RECEIPTS(bailID);
      console.log("URL de génération:", endpoint);
      
      const response = await fetchGet(endpoint);
      
      // Extract tenant ID from the result if available
      if (response && response.tenant_id && !response.tenantId) {
        response.tenantId = response.tenant_id; // Normalize key name
      }
      
      return response;
    } catch (error) {
      console.error('Error generating receipts:', error);
      throw error;
    }
  },

  // Update invoice version
  updateInvoiceVersion: async (factureId, currentVersion) => {
    try {
      // Log exactly what we received for debugging
      console.log('updateInvoiceVersion called with:', {
        factureId,
        currentVersion,
        typeFactureId: typeof factureId,
        typeCurrentVersion: typeof currentVersion
      });
      
      if (!factureId) {
        throw new Error('Invoice ID is required');
      }
      
      // Explicitly handle currentVersion
      // Empty string, undefined or null will be treated as 'standard'
      const isV2 = currentVersion === 'v2';
      const newVersion = isV2 ? '' : 'v2';
      
      console.log(`Changing invoice ${factureId} from ${isV2 ? 'v2' : 'standard'} to ${newVersion || 'standard'}`);
      
      // Use the configured API endpoint instead of hardcoded URL
      const url = ACTIVE_API.UPDATE_INVOICE_VERSION;
      console.log('Using API endpoint:', url);
      
      const data = {
        id: factureId,
        version: newVersion
      };
      
      console.log('Sending data:', data);
      
      // No nested try-catch - let errors bubble up naturally
      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      
      const result = await handleResponse(response);
      console.log('API response:', result);
      
      // Extract tenant ID from the result if available
      // If not, we'll need to handle it at the context level
      if (result && result.tenant_id && !result.tenantId) {
        result.tenantId = result.tenant_id; // Normalize key name
      }
      
      return result;
    } catch (error) {
      console.error('Error in updateInvoiceVersion:', error);
      throw error;
    }
  },

  // Delete invoice
  deleteInvoice: async (invoiceId, version) => {
    try {
      // Utiliser un seul endpoint mais passer la version comme paramètre
      const endpoint = `https://admin.hlwc.fr/public/delete-facture?id=${invoiceId}${version === 'v2' ? '&version=v2' : ''}`;
      
      console.log(`Suppression de facture ${invoiceId} (version: ${version || 'standard'})`, endpoint);
      
      const response = await fetchGet(endpoint);
      console.log("Réponse de l'API pour deleteInvoice:", response);
      
      // Extract tenant ID from the result if available
      if (response && response.tenant_id && !response.tenantId) {
        response.tenantId = response.tenant_id; // Normalize key name
      }
      
      return response;
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  },

  // Create transaction
  createTransaction: async (transactionData) => {
    try {
      return await fetchPost(ACTIVE_API.TRANSACTION, transactionData);
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw error;
    }
  },
  
  // Update bail start date
  updateBailStartDate: async (bailID, newStartDate) => {
    try {
      // Format pour l'API n8n
      const data = {
        id: bailID,
        new_start_date: newStartDate
      };
      
      console.log("Mise à jour de la date de début:", data);
      
      return await fetchPost(ACTIVE_API.UPDATE_BAIL, data);
    } catch (error) {
      console.error('Error updating bail start date:', error);
      throw error;
    }
  },
  
  // Update bail end date
  updateBailEndDate: async (bailID, newEndDate) => {
    try {
      // Format pour l'API n8n
      const data = {
        id: bailID,
        new_end_date: newEndDate
      };
      
      console.log("Mise à jour de la date de fin:", data);
      
      return await fetchPost(ACTIVE_API.UPDATE_BAIL, data);
    } catch (error) {
      console.error('Error updating bail end date:', error);
      throw error;
    }
  },
  
  // Update facture cloture
  updateFactureCloture: async (bailID, montant) => {
    try {
      // Format pour l'API n8n
      const data = {
        id: bailID,
        facture_cloture: montant,
        table_name: 'baux'
      };
      
      console.log("Mise à jour du montant de facture de clôture:", data);
      
      return await fetchPost(ACTIVE_API.UPDATE_BAIL, data);
    } catch (error) {
      console.error('Error updating facture cloture:', error);
      throw error;
    }
  }
};

export default apiService;
